/*
 * Real Installs — shared client module
 * Used by sections/ss-real-installs.liquid (PDP strip) and
 * sections/ss-real-installs-band.liquid (homepage band).
 *
 * Self-booting: scans for [data-ss-ri] roots and initialises each by kind.
 * Fetches the public gallery API once per page (cached promise) and builds cards
 * by cloning the <template> from snippets/ss-real-installs-card.liquid.
 * Fails soft: on any error the section stays hidden (renders nothing).
 */
window.SSRealInstalls = window.SSRealInstalls || (function () {
  var ENDPOINT = 'https://zepxdcuapsylogshaawd.supabase.co/functions/v1/projects-read';
  var GALLERY = 'https://app.sleekspace.com.au/projects';
  var WIDTHS = [400, 600, 800];
  var _promise = null;

  /* Every store→app link carries ?from=store so the gallery latches "store mode" — a focused
     shopper view (no app nav, no signup prompts). See tile-designer-pro src/lib/store-mode.ts. */
  /* An install with an As Seen On story links to the story (snippets/ss-story-map.liquid). */
  var _stories = null;
  function storyUrl(handle) {
    if (!_stories) {
      try { _stories = JSON.parse(document.querySelector('script[data-ss-story-map]').textContent).gallery || {}; } catch (e) { _stories = {}; }
    }
    return _stories[handle] ? '/blogs/as-seen-on/' + _stories[handle] : '';
  }

  function galleryUrl(handle) {
    return GALLERY + (handle ? '/' + encodeURIComponent(handle) : '') + '?from=store';
  }

  /* Fetch all published installs once. Body {} → featured-first then newest (<=200). */
  function fetchProjects() {
    if (_promise) return _promise;
    _promise = fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}'
    })
      .then(function (r) {
        if (!r.ok) throw new Error('projects-read HTTP ' + r.status);
        return r.json();
      })
      .then(function (d) {
        return (d && Array.isArray(d.projects)) ? d.projects : [];
      })
      .catch(function (e) {
        if (window.console && console.warn) console.warn('[ss-real-installs]', e);
        return [];
      });
    return _promise;
  }

  function isHex(s) {
    return typeof s === 'string' && /^#?[0-9a-fA-F]{3,8}$/.test(s);
  }

  /* Normalise a SKU for exact comparison: upper-case first, then strip a trailing pack
     suffix (CTN/BAG). Product variant SKUs are already clean base SKUs so the strip is
     defensive; project baseSkus arrive pre-stripped. Upper-case BEFORE the strip so a
     lower-cased "…ctn" still matches /(CTN|BAG)$/. */
  function normSku(s) {
    return String(s == null ? '' : s).trim().toUpperCase().replace(/(CTN|BAG)$/, '');
  }

  /* A project's base SKUs. projects-read only returns whitelisted columns, so a top-level
     baseSkus is dropped — the real SKUs ride the products jsonb at products[].baseSkus.
     Read both: nested is the source, top-level is a defensive fallback. */
  function projSkus(p) {
    var out = Array.isArray(p.baseSkus) ? p.baseSkus.slice() : [];
    if (Array.isArray(p.products)) p.products.forEach(function (e) { if (e && Array.isArray(e.baseSkus)) out = out.concat(e.baseSkus); });
    return out;
  }

  function srcset(stem, fmt) {
    return WIDTHS.map(function (w) { return stem + '-' + w + '.' + fmt + ' ' + w + 'w'; }).join(', ');
  }

  function hasVideo(p) {
    return Array.isArray(p.media) && p.media.some(function (m) { return m && m.type === 'video'; });
  }

  /* Photos with the "before" shots removed — cards and modal show the finished result. */
  function afterPhotos(p) {
    var ph = (p && p.photos) || [];
    var out = ph.filter(function (x) { return x && x.role !== 'before'; });
    return out.length ? out : ph;
  }

  /* Auto-scroll a marquee track. The track is a native overflow-x scroller, so drag / wheel /
     swipe all work; this just nudges scrollLeft each frame and pauses on hover or touch. Loops
     seamlessly at the half-way point (the card set is rendered twice). Respects reduced-motion. */
  function initMarquee(track) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var paused = false, last = 0, resume, pos = 0;
    function hold(ms) { paused = true; clearTimeout(resume); if (ms) resume = setTimeout(function () { paused = false; }, ms); }

    /* Mouse click-and-drag to slide (desktop overflow containers have no native drag; touch
       keeps native scroll). A real drag swallows the following click so it doesn't open the modal. */
    var down = false, startX = 0, startScroll = 0, moved = 0;
    track.style.cursor = 'grab';
    track.style.userSelect = 'none';
    // Cards are <a>/<img> — natively drag-and-droppable, which hijacks the slide on card area.
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });
    track.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') hold(0); });
    track.addEventListener('pointerleave', function () { if (!down) paused = false; });
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      down = true; moved = 0; startX = e.clientX; startScroll = track.scrollLeft; hold(0);
      track.style.cursor = 'grabbing';
      if (track.setPointerCapture) { try { track.setPointerCapture(e.pointerId); } catch (x) {} }
    });
    track.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX; moved = Math.abs(dx);
      track.scrollLeft = startScroll - dx; pos = track.scrollLeft;
    });
    function endDrag() {
      if (!down) return;
      down = false; track.style.cursor = 'grab'; hold(1500);
      if (moved > 6) {
        var sw = function (ev) { ev.stopPropagation(); ev.preventDefault(); };
        track.addEventListener('click', sw, true);
        setTimeout(function () { track.removeEventListener('click', sw, true); }, 60);
      }
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('touchstart', function () { hold(0); }, { passive: true });
    track.addEventListener('touchend', function () { hold(2500); }, { passive: true });
    track.addEventListener('wheel', function () { hold(1500); }, { passive: true });

    function step(ts) {
      if (!last) last = ts;
      var dt = ts - last; last = ts;
      var half = track.scrollWidth / 2;
      if (!paused && dt < 100 && half > 0) {
        pos += dt * 0.05;              // float accumulator — scrollLeft floors sub-pixels
        if (pos >= half) pos -= half;  // seamless loop at the half-way point
        track.scrollLeft = pos;
      } else {
        pos = track.scrollLeft;        // stay in sync while paused or user-scrolling
      }
      requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  /* Clone the card template for one project. Returns the node, or null if unusable. */
  function buildCard(p, tpl, opts) {
    opts = opts || {};
    if (!p || !p.handle) return null;
    var photos = afterPhotos(p);
    var photo = photos[0] || null;
    if (!photo || !photo.renderUrl) return null; // no usable (non-before) image → skip

    var root = tpl.content.firstElementChild.cloneNode(true);
    var stem = photo.renderUrl;

    root.href = storyUrl(p.handle) || galleryUrl(p.handle);

    var media = root.querySelector('[data-ri-media]');
    if (media && isHex(photo.placeholder)) {
      media.style.backgroundColor = photo.placeholder.charAt(0) === '#' ? photo.placeholder : '#' + photo.placeholder;
    }

    var avif = root.querySelector('[data-ri-avif]');
    var webp = root.querySelector('[data-ri-webp]');
    var img = root.querySelector('[data-ri-img]');
    if (avif) avif.srcset = srcset(stem, 'avif');
    if (webp) webp.srcset = srcset(stem, 'webp');
    if (img) {
      // Some projects expose a renderUrl whose derivatives don't exist yet. <picture>
      // won't fall through on a 404 (fallback is by format support, not load success),
      // so drop the card rather than show a broken tile — and if that empties the group
      // or section (async, after the synchronous reveal), re-hide it so we never leave a
      // revealed-but-empty shell.
      img.onerror = function () {
        var sec = root.closest ? root.closest('[data-ss-ri]') : null;
        var grp = root.closest ? root.closest('[data-group]') : null;
        if (root.parentNode) root.parentNode.removeChild(root);
        if (grp && !grp.querySelector('.ss-ri-card')) grp.hidden = true;
        if (sec && !sec.querySelector('.ss-ri-card')) sec.hidden = true;
      };
      img.src = stem + '-800.jpg'; // single jpg derivative is the universal fallback
      img.alt = photo.alt || ((p.firstName ? p.firstName + "'s " : '') + 'garage');
    }

    var name = root.querySelector('[data-ri-name]');
    if (name) {
      var bits = [];
      if (p.firstName) bits.push(p.firstName);
      if (p.state) bits.push(p.state);
      name.textContent = bits.join(' · ');
    }

    // Ambassador identity caption (name + bio) — surfaced only for kind=ambassador.
    if (p.kind === 'ambassador') {
      var amb = root.querySelector('[data-ri-amb]');
      if (amb) {
        var an = amb.querySelector('[data-ri-amb-name]');
        var ab = amb.querySelector('[data-ri-amb-bio]');
        if (an) an.textContent = p.firstName || '';
        if (ab) { if (p.bio) { ab.textContent = p.bio; } else { ab.hidden = true; } }
        amb.hidden = false;
      }
    }

    root.__ri = p; // stash the project so a card click can open the lightbox
    return root;
  }

  /* Build cards for `projects` and append them to `track`. Returns the count rendered. */
  function renderInto(track, projects, opts) {
    if (!track) return 0;
    var tpl = document.getElementById('ss-ri-card-tpl');
    if (!tpl || !tpl.content) return 0;
    var frag = document.createDocumentFragment();
    var n = 0;
    projects.forEach(function (p) {
      var card = buildCard(p, tpl, opts);
      if (card) { frag.appendChild(card); n++; }
    });
    track.appendChild(frag);
    return n;
  }

  /* ---- PDP strip: exact base-SKU primary + honest range/other browse rows ---- */
  function initPDP(root) {
    var range = root.getAttribute('data-range') || '';
    var colour = root.getAttribute('data-colour') || '';
    var max = parseInt(root.getAttribute('data-max'), 10) || 12;
    var featuredTag = root.getAttribute('data-featured-tag') === 'true';
    var rangeOnly = root.getAttribute('data-range-only') === 'true'; // kits/bundles: match the range, not a tile SKU

    /* Product's base SKU(s) as a lookup set. Sourced from the variant SKU(s) (never the
       handle) and normalised. Normally one clean base SKU, e.g. UGPDGY001. */
    var productSkus = {};
    (root.getAttribute('data-skus') || '').split(',').forEach(function (s) {
      var k = normSku(s);
      if (k) productSkus[k] = true;
    });

    fetchProjects().then(function (all) {
      if (!all.length) return;
      var seen = {};
      function fresh(list) {
        return list.filter(function (p) {
          if (!p.handle || seen[p.handle]) return false;
          seen[p.handle] = true;
          return true;
        });
      }
      function inRange(p) { return range && p.ranges && p.ranges.indexOf(range) > -1; }
      function inColour(p) { return colour && p.colours && p.colours.indexOf(colour) > -1; }
      /* Exact base-SKU hit against the project's base SKUs (range + colour both encoded). */
      function skuExact(p) {
        var bs = projSkus(p);
        if (!bs.length) return false;
        return bs.some(function (s) { return productSkus[normSku(s)]; });
      }
      /* Primary = strict SKU match. Rollout-safe: a project with no base SKUs yet falls
         back to the old range+colour text match — colour kept so colours never bleed. */
      function primaryMatch(p) {
        if (projSkus(p).length) return skuExact(p);
        return inRange(p) && (colour ? inColour(p) : true);
      }

      var groups;
      if (rangeOnly) {
        /* Kits/bundles have no tile base SKU to match — show the underlying range directly
           as the primary group (the kit's variant SKU isn't a tile SKU, so exact match can't fire). */
        groups = range ? [{ key: 'primary', label: 'Real ' + range + ' installs', items: all.filter(inRange) }] : [];
      } else {
        var primaryLabel = colour ? ('Real installs in ' + colour)
          : (range ? ('Real ' + range + ' installs') : 'Real installs with this product');
        groups = [{ key: 'primary', label: primaryLabel, items: all.filter(primaryMatch) }];
        /* Same-range top-up (other colours) — clearly labelled and separate from the exact
           matches. No cross-range "Other ranges" row. Only built when we know the range. */
        if (range) {
          groups.push({ key: 'topa', label: 'More ' + range + ' installs', items: all.filter(function (p) { return inRange(p) && !primaryMatch(p); }) });
        }
      }

      var total = 0;
      groups.forEach(function (g) {
        var el = root.querySelector('[data-group="' + g.key + '"]');
        if (!el) return;
        var items = fresh(g.items).slice(0, Math.max(0, max - total));
        if (!items.length) return;
        var n = renderInto(el.querySelector('[data-ri-track]'), items, { featuredTag: featuredTag });
        if (n > 0) {
          var lab = el.querySelector('[data-ri-label]');
          if (lab) lab.textContent = g.label;
          el.hidden = false;
          total += n;
        }
      });
      if (total > 0) root.hidden = false;
    });
  }

  /* Case-insensitive membership (ranges are ALL-CAPS, categories lower-case). */
  function hasCI(arr, val) {
    if (!arr || !val) return false;
    val = String(val).toLowerCase();
    return arr.some(function (x) { return String(x).toLowerCase() === val; });
  }

  /* ---- Flexible row: featured / handpicked / category / range / single ---- */
  function initRow(root) {
    var source = root.getAttribute('data-source') || 'featured';
    var layout = root.getAttribute('data-layout') || 'masonry';
    var space = root.getAttribute('data-space') || '';
    var pin = root.getAttribute('data-pin') || '';
    var max = parseInt(root.getAttribute('data-max'), 10) || 8;
    var featuredTag = root.getAttribute('data-featured-tag') === 'true';
    var category = root.getAttribute('data-category') || '';
    var range = root.getAttribute('data-range') || '';
    var handles = (root.getAttribute('data-handles') || '')
      .split(/[\s,]+/).map(function (s) { return s.trim(); }).filter(Boolean);

    fetchProjects().then(function (all) {
      if (!all.length) return;
      var chosen;
      if (source === 'handpicked' || source === 'single') {
        var byHandle = {};
        all.forEach(function (p) { if (p.handle) byHandle[p.handle] = p; });
        var seen = {};
        chosen = handles
          .map(function (h) { return byHandle[h]; }) // explicit curator order
          .filter(function (p) { if (!p || seen[p.handle]) return false; seen[p.handle] = true; return true; });
      } else if (source === 'category') {
        chosen = all.filter(function (p) { return hasCI(p.categories, category); });
      } else if (source === 'range') {
        chosen = all.filter(function (p) { return hasCI(p.ranges, range); });
      } else if (source === 'spaceType') {
        chosen = all.filter(function (p) { return hasCI([p.spaceType], space); });
        // Thin/empty space (man-cave, home-gym, events pre-enrichment) → backfill with garage
        // installs so the landing is never bare. Real space installs stay first and grow in as
        // bot-ai tags them; no-op once the space itself has >= max. Garage landings skip this.
        if (chosen.length < max && space.toLowerCase() !== 'garage') {
          var have = {};
          chosen.forEach(function (p) { if (p.handle) have[p.handle] = true; });
          chosen = chosen.concat(all.filter(function (p) {
            return hasCI([p.spaceType], 'garage') && !(p.handle && have[p.handle]);
          }));
        }
      } else {
        chosen = all.filter(function (p) { return p.featured; }); // featured (API order)
      }
      if (pin) {
        for (var pk = 0; pk < chosen.length; pk++) {
          if (chosen[pk].handle === pin) { chosen.unshift(chosen.splice(pk, 1)[0]); break; }
        }
      }
      if (source === 'single') max = 1;
      chosen = chosen.slice(0, max);
      if (!chosen.length) return;

      var track = root.querySelector('[data-ri-track]');
      var opts = { featuredTag: featuredTag };

      if (layout === 'hero') {
        // Feature install (prefer one that carries a quote) + quote panel + thumb strip.
        var fi = 0;
        for (var i = 0; i < chosen.length; i++) { if (chosen[i].quote) { fi = i; break; } }
        var feat = chosen[fi];
        var featWrap = document.createElement('div');
        featWrap.className = 'ss-rib__feature';
        renderInto(featWrap, [feat], opts);
        track.appendChild(featWrap);
        if (feat.quote) {
          var q = document.createElement('div');
          q.className = 'ss-rib__quote';
          var rr = Math.round(feat.rating || 0);
          var stars = rr > 0 ? '<span class="ss-rib__quote-stars" aria-label="' + rr + ' out of 5 stars">' + new Array(rr + 1).join('★') + '</span>' : '';
          q.innerHTML =
            '<span class="ss-rib__quote-mark" aria-hidden="true">“</span>' +
            '<p class="ss-rib__quote-txt"></p>' +
            '<div class="ss-rib__quote-foot"><span class="ss-rib__quote-who"></span>' + stars + '</div>';
          q.querySelector('.ss-rib__quote-txt').textContent = feat.quote;
          var who = [];
          if (feat.firstName) who.push(feat.firstName);
          if (feat.state) who.push(feat.state);
          q.querySelector('.ss-rib__quote-who').textContent = who.join(' · ');
          track.appendChild(q);
        }
        var thumbs = document.createElement('div');
        thumbs.className = 'ss-rib__thumbs';
        var tn = renderInto(thumbs, chosen.filter(function (_, j) { return j !== fi; }).slice(0, 6), opts);
        track.appendChild(thumbs);
        if (featWrap.querySelector('.ss-ri-card') || tn > 0) root.hidden = false;
        return;
      }

      var n = renderInto(track, chosen, opts);
      if (n > 0) {
        if (layout === 'marquee') { renderInto(track, chosen, opts); initMarquee(track); } // 2nd set + auto-scroll
        root.hidden = false;
      }
    });
  }

  function initRoot(root) {
    if (root.__ssriInit) return;
    root.__ssriInit = true;
    var kind = root.getAttribute('data-ss-ri');
    if (kind === 'pdp') initPDP(root);
    else if (kind === 'row') initRow(root);
  }

  function boot(scope) {
    var nodes = (scope || document).querySelectorAll('[data-ss-ri]');
    Array.prototype.forEach.call(nodes, initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  /* Shopify theme editor: (re)initialise a section when it loads */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  /* ---------- Shared lightbox modal (opened from any .ss-ri-card, PDP or band) ---------- */
  var _modal = null;
  var _lastFocus = null;

  function ensureModal() {
    if (_modal) return _modal;
    var el = document.getElementById('ss-ri-modal');
    if (!el) {
      el = document.createElement('div');
      el.className = 'ss-rimodal';
      el.id = 'ss-ri-modal';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Real customer install');
      el.hidden = true;
      el.innerHTML =
        '<div class="ss-rimodal__overlay" data-ri-close></div>' +
        '<div class="ss-rimodal__panel" tabindex="-1">' +
          '<button class="ss-rimodal__x" type="button" data-ri-close aria-label="Close">' +
            '<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
          '<div class="ss-rimodal__media"><picture>' +
            '<source data-ri-m-avif type="image/avif"><source data-ri-m-webp type="image/webp">' +
            '<img data-ri-m-img alt="" decoding="async">' +
          '</picture></div>' +
          '<div class="ss-rimodal__thumbs" data-ri-thumbs></div>' +
          '<div class="ss-rimodal__body">' +
            '<p class="ss-rimodal__name" data-ri-m-name></p>' +
            '<p class="ss-rimodal__sub" data-ri-m-sub></p>' +
            '<a class="ss-rimodal__cta" data-ri-m-cta href="#">See the full project' +
              '<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>' +
            '</a>' +
          '</div>' +
        '</div>';
      document.body.appendChild(el);
    }
    // Bind once, whether we created the modal or found an existing node. Dismiss on the X or the
    // overlay, and on any click that lands outside the card panel — tap the backdrop to close.
    if (!el._riBound) {
      el.addEventListener('click', function (e) {
        if (e.target.closest('[data-ri-close]') || !e.target.closest('.ss-rimodal__panel')) {
          closeModal();
        }
      });
      el._riBound = true;
    }
    _modal = el;
    return el;
  }

  function setModalPhoto(m, photo, altText) {
    var stem = photo && photo.renderUrl;
    if (!stem) return;
    var avif = m.querySelector('[data-ri-m-avif]');
    var webp = m.querySelector('[data-ri-m-webp]');
    var img = m.querySelector('[data-ri-m-img]');
    if (avif) avif.srcset = srcset(stem, 'avif');
    if (webp) webp.srcset = srcset(stem, 'webp');
    if (img) { img.src = stem + '-800.jpg'; img.alt = altText || ''; }
  }

  function openModal(p) {
    if (!p) return;
    var m = ensureModal();
    var photos = afterPhotos(p);
    var alt = (p.firstName ? p.firstName + "'s " : '') + 'garage';
    if (photos[0]) setModalPhoto(m, photos[0], alt);

    var name = m.querySelector('[data-ri-m-name]');
    if (name) {
      var bits = [];
      if (p.firstName) bits.push(p.firstName);
      if (p.state) bits.push(p.state);
      name.textContent = bits.join(' · ') || 'Real install';
    }
    var sub = m.querySelector('[data-ri-m-sub]');
    if (sub) {
      var parts = [];
      if (p.ranges && p.ranges.length) parts.push(p.ranges.join(', '));
      if (p.colours && p.colours.length) parts.push(p.colours.join(', '));
      sub.textContent = parts.join(' · ');
    }
    var cta = m.querySelector('[data-ri-m-cta]');
    if (cta && p.handle) {
      var story = storyUrl(p.handle);
      cta.href = story || galleryUrl(p.handle);
      cta.firstChild.nodeValue = story ? 'Read the full story' : 'See the full project';
    }

    var thumbs = m.querySelector('[data-ri-thumbs]');
    if (thumbs) {
      thumbs.innerHTML = '';
      if (photos.length > 1) {
        photos.slice(0, 6).forEach(function (ph) {
          if (!ph || !ph.renderUrl) return;
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'ss-rimodal__thumb';
          b.setAttribute('aria-current', ph === photos[0] ? 'true' : 'false');
          b.innerHTML = '<picture>' +
            '<source type="image/avif" srcset="' + ph.renderUrl + '-400.avif">' +
            '<source type="image/webp" srcset="' + ph.renderUrl + '-400.webp">' +
            '<img src="' + ph.renderUrl + '-800.jpg" alt="" decoding="async">' +
            '</picture>';
          b.addEventListener('click', function () {
            setModalPhoto(m, ph, alt);
            Array.prototype.forEach.call(thumbs.querySelectorAll('.ss-rimodal__thumb'), function (t) { t.setAttribute('aria-current', 'false'); });
            b.setAttribute('aria-current', 'true');
          });
          thumbs.appendChild(b);
        });
      }
    }

    _lastFocus = document.activeElement;
    m.hidden = false;
    document.body.classList.add('ss-ri-modal-open');
    var panel = m.querySelector('.ss-rimodal__panel');
    if (panel) panel.focus();
  }

  function closeModal() {
    if (!_modal || _modal.hidden) return;
    _modal.hidden = true;
    document.body.classList.remove('ss-ri-modal-open');
    if (_lastFocus && _lastFocus.focus) _lastFocus.focus();
    _lastFocus = null;
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.keyCode === 27) closeModal();
  });

  /* Intercept card clicks → open the lightbox instead of navigating. The card stays a real
     <a> (its href is the no-JS fallback + the modal's link out). Modifier/middle clicks keep
     their native "open in new tab" behaviour. */
  document.addEventListener('click', function (e) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button && e.button !== 0)) return;
    var card = e.target.closest && e.target.closest('.ss-ri-card');
    if (!card || !card.__ri) return;
    e.preventDefault();
    openModal(card.__ri);
  });

  return {
    fetchProjects: fetchProjects,
    buildCard: buildCard,
    renderInto: renderInto,
    hasVideo: hasVideo,
    boot: boot,
    openModal: openModal,
    closeModal: closeModal,
    GALLERY: GALLERY
  };
})();
